import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { normalizeDigits } from "../../../infrastructure/utils/formatters";

const DEFAULT_INPUT_STYLE = {
  fontFamily: "monospace",
  MozAppearance: "textfield",
  borderRadius: "6px",
  border: "1px solid",
  boxShadow: "0px 0px 10px 0px rgba(0,0,0,.10)",
  margin: "4px",
  paddingLeft: "8px",
  width: "36px",
  height: "42px",
  fontSize: "32px",
  boxSizing: "border-box",
  textAlign: "center",
  outline: "none",
};

const DEFAULT_INVALID_STYLE = {
  color: "#b94a48",
  backgroundColor: "#f2dede",
  borderColor: "#eed3d7",
};

const OtpCodeInput = ({
  fields = 5,
  value = "",
  onChange,
  disabled = false,
  isValid = true,
  autoFocus = true,
  inputMode = "numeric",
  pattern,
  style,
  inputStyle,
  inputStyleInvalid,
  name,
}) => {
  const inputRefs = useRef([]);

  const chars = useMemo(() => {
    const normalized = normalizeDigits(String(value || "")).slice(0, fields);
    return Array.from({ length: fields }, (_, index) => normalized[index] || "");
  }, [fields, value]);

  const resolvedInputStyle = useMemo(
    () => ({
      ...DEFAULT_INPUT_STYLE,
      color: "black",
      backgroundColor: "white",
      borderColor: "lightgrey",
      ...inputStyle,
      ...(isValid ? {} : { ...DEFAULT_INVALID_STYLE, ...inputStyleInvalid }),
      ...(disabled
        ? {
            cursor: "not-allowed",
            color: "lightgrey",
            borderColor: "lightgrey",
            backgroundColor: "#efeff1",
          }
        : {}),
    }),
    [disabled, inputStyle, inputStyleInvalid, isValid]
  );

  const focusIndex = useCallback((index) => {
    const input = inputRefs.current[index];
    if (!input) return;
    input.focus();
    input.select();
  }, []);

  const updateValue = useCallback(
    (nextChars) => {
      onChange?.(nextChars.join("").slice(0, fields));
    },
    [fields, onChange]
  );

  const handleChange = useCallback(
    (index, rawValue) => {
      const digits = normalizeDigits(rawValue).replace(/\D/g, "");
      const next = [...chars];

      if (!digits) {
        next[index] = "";
        updateValue(next);
        return;
      }

      if (digits.length > 1) {
        digits.split("").forEach((digit, offset) => {
          const position = index + offset;
          if (position < fields) next[position] = digit;
        });
      } else {
        next[index] = digits;
      }

      updateValue(next);

      const filledCount = digits.length > 1 ? digits.length : 1;
      const nextFocus = Math.min(index + filledCount, fields - 1);
      if (nextFocus !== index || digits.length > 1) {
        focusIndex(nextFocus);
      }
    },
    [chars, fields, focusIndex, updateValue]
  );

  const handleKeyDown = useCallback(
    (index, event) => {
      if (event.key === "Backspace") {
        event.preventDefault();
        const next = [...chars];
        if (next[index]) {
          next[index] = "";
          updateValue(next);
          return;
        }
        if (index > 0) {
          next[index - 1] = "";
          updateValue(next);
          focusIndex(index - 1);
        }
        return;
      }

      if (event.key === "ArrowLeft" && index > 0) {
        event.preventDefault();
        focusIndex(index - 1);
        return;
      }

      if (event.key === "ArrowRight" && index < fields - 1) {
        event.preventDefault();
        focusIndex(index + 1);
      }
    },
    [chars, fields, focusIndex, updateValue]
  );

  const handlePaste = useCallback(
    (index, event) => {
      event.preventDefault();
      const pasted = normalizeDigits(event.clipboardData.getData("text") || "").replace(
        /\D/g,
        ""
      );
      if (!pasted) return;

      const next = [...chars];
      pasted.split("").forEach((digit, offset) => {
        const position = index + offset;
        if (position < fields) next[position] = digit;
      });
      updateValue(next);
      focusIndex(Math.min(index + pasted.length, fields - 1));
    },
    [chars, fields, focusIndex, updateValue]
  );

  useEffect(() => {
    if (autoFocus && !disabled) {
      focusIndex(0);
    }
  }, [autoFocus, disabled, focusIndex]);

  return (
    <div
      className="react-code-input"
      style={{ display: "inline-block", direction: "ltr", ...style }}
    >
      {chars.map((char, index) => (
        <input
          key={`${name || "otp"}-${index}`}
          ref={(element) => {
            inputRefs.current[index] = element;
          }}
          type="tel"
          inputMode={inputMode}
          pattern={pattern}
          name={name ? `${name}-${index}` : undefined}
          value={char}
          disabled={disabled}
          autoComplete={index === 0 ? "one-time-code" : "off"}
          maxLength={1}
          style={resolvedInputStyle}
          onChange={(event) => handleChange(index, event.target.value)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onPaste={(event) => handlePaste(index, event)}
          onFocus={(event) => event.target.select()}
          aria-label={`رقم ${index + 1}`}
        />
      ))}
    </div>
  );
};

OtpCodeInput.propTypes = {
  fields: PropTypes.number,
  value: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  isValid: PropTypes.bool,
  autoFocus: PropTypes.bool,
  inputMode: PropTypes.string,
  pattern: PropTypes.string,
  style: PropTypes.object,
  inputStyle: PropTypes.object,
  inputStyleInvalid: PropTypes.object,
  name: PropTypes.string,
};

export default OtpCodeInput;
